import { useEffect, useRef } from 'react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { getInsight, getStateHash } from '../../lib/ai';
import { INSIGHT_PROMPTS } from '../../data/aiContext';
import type { TabId } from '../../types';

const ALL_TABS = Object.keys(INSIGHT_PROMPTS);

/**
 * Invisible component that prefetches all tab insights in parallel
 * on mount and whenever assumptions/packages/price change.
 */
export function InsightPreloader() {
  const assumptions = useSimulatorStore(s => s.assumptions);
  const packages = useSimulatorStore(s => s.packages);
  const selectedPrice = useSimulatorStore(s => s.selectedPrice);

  const lastHash = useRef('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const inFlight = useRef(false);

  const currentState = { assumptions, packages, activeTab: 'summary' as TabId, selectedPrice };
  const hash = getStateHash(currentState);

  useEffect(() => {
    if (hash === lastHash.current) return;
    lastHash.current = hash;

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (inFlight.current) return;
      inFlight.current = true;

      const store = useSimulatorStore.getState();

      Promise.allSettled(
        ALL_TABS.map(async (tabId) => {
          const state = {
            assumptions: store.assumptions,
            packages: store.packages,
            activeTab: tabId as TabId,
            selectedPrice: store.selectedPrice,
          };
          const stateHash = getStateHash(state);
          const cached = useSimulatorStore.getState().insightCache[tabId];
          if (cached?.hash === stateHash) return;
          try {
            const result = await getInsight(tabId, state);
            useSimulatorStore.getState().setCachedInsight(tabId, stateHash, result);
          } catch {
            // individual tab failure; InsightCard will retry on demand
          }
        })
      ).finally(() => {
        inFlight.current = false;
      });
    }, 800);

    return () => clearTimeout(debounceRef.current);
  }, [hash]);

  return null;
}
